// --- S3_FEATURE_START ---
// Public-file URLs — client-safe (no storage, no database). The server side is
// `src/api/public-files.ts`; the rule is `.claude/rules/storage.md` → Public files.

/**
 * The stable public URL of a file: `/f/<id>/<name>`. `name` is cosmetic — a
 * readable URL and the download name — and is never used to find the file.
 * The URL works only while the file's visibility is `public`.
 */
export function publicFileUrl(id: string, name: string, opts: { size?: "thumb" } = {}): string {
  const path = `/f/${encodeURIComponent(id)}/${encodeURIComponent(name || "file")}`;
  return opts.size === "thumb" ? `${path}?size=thumb` : path;
}

/**
 * The file id behind one of OUR public URLs (`/f/<id>/…`), or null for anything
 * else — an OAuth provider's URL, a bare file id from before public files, "".
 */
export function fileIdFromPublicUrl(url: string | null | undefined): string | null {
  const match = /^\/f\/([^/?#]+)\//.exec(url ?? "");
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

interface AvatarSteps {
  setImage: (url: string) => Promise<unknown>;
  /**
   * The user's image as the SERVER has it now, read at submit time — not the
   * value the card rendered with, which a second tab may have made stale (and
   * retiring a stale "previous" photo could hide the one actually in use).
   */
  currentImage: () => Promise<string | null | undefined>;
  /** Make a file private again. A failure to retire never undoes the change. */
  makePrivate: (fileId: string) => Promise<unknown>;
}

/**
 * Take the previous avatar out of public view, AFTER the user's image has moved
 * off it — so the avatar never points at a file that just went private. Returns
 * whether it had to, and whether that worked; a failure here is reported, not
 * thrown, because the avatar change itself has already happened.
 */
async function retirePrevious(
  previousImage: string | null | undefined,
  keepFileId: string | null,
  steps: Pick<AvatarSteps, "makePrivate">,
): Promise<{ retired: string | null; retireError: unknown }> {
  const previous = fileIdFromPublicUrl(previousImage);
  if (!previous || previous === keepFileId) return { retired: null, retireError: null };
  try {
    await steps.makePrivate(previous);
    return { retired: previous, retireError: null };
  } catch (error) {
    return { retired: null, retireError: error };
  }
}

/**
 * The avatar flow, the template's worked example of a public file: upload the
 * image, make it public, THEN point the user's image at its public URL — in
 * that order, so the avatar never shows a broken image for the moment between —
 * and finally make the photo it REPLACED private again, so a replaced photo is
 * not left reachable at a public URL. If pointing the image fails, the new photo
 * is made private again, so nothing public is left that nothing points at. The
 * thumbnail is what an avatar needs (~400px WebP).
 */
export async function publishAvatar(
  file: File,
  steps: AvatarSteps & {
    upload: (file: File) => Promise<{ id: string }>;
    makePublic: (fileId: string) => Promise<unknown>;
  },
): Promise<{ url: string; retired: string | null; retireError: unknown }> {
  const record = await steps.upload(file);
  await steps.makePublic(record.id);
  const url = publicFileUrl(record.id, "avatar.webp", { size: "thumb" });
  const previousImage = await steps.currentImage();
  try {
    await steps.setImage(url);
  } catch (error) {
    // Best effort: the original failure is the one worth reporting.
    await steps.makePrivate(record.id).catch(() => {});
    throw error;
  }
  return { url, ...(await retirePrevious(previousImage, record.id, steps)) };
}

/** "Remove photo": clear the user's image, then make the removed photo private. */
export async function removeAvatar(
  steps: AvatarSteps,
): Promise<{ retired: string | null; retireError: unknown }> {
  const previousImage = await steps.currentImage();
  await steps.setImage("");
  return retirePrevious(previousImage, null, steps);
}
// --- S3_FEATURE_END ---
